import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import { sendWhatsAppTemplate } from '../../services/whatsapp.service'
import {
  replaceTemplatePlaceholders,
  WHATSAPP_TEMPLATE_PARAM_LABELS,
  WHATSAPP_TEMPLATES,
} from '../../utils/whatsappTemplates'

export default function WhatsAppTestMessageForm() {
  const [template, setTemplate] = useState('')
  const [to, setTo] = useState('')
  const [templateParams, setTemplateParams] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [feedback, setFeedback] = useState(null)

  const templateConfig = useMemo(() => (template ? WHATSAPP_TEMPLATES[template] : null), [template])
  const requiredParams = templateConfig?.params || []

  const previewText = useMemo(() => {
    if (!templateConfig?.preview) {
      return 'Selecciona una plantilla para ver su vista previa.'
    }

    return replaceTemplatePlaceholders(templateConfig.preview, templateParams)
  }, [templateConfig, templateParams])

  const handleTemplateParamChange = (field, value) => {
    setTemplateParams((currentParams) => ({ ...currentParams, [field]: value }))
  }

  const validateForm = () => {
    if (!to.trim()) return 'Ingresa número destino. Ej: +56912345678'
    if (!template.trim()) return 'Debes seleccionar una plantilla.'

    const missingField = requiredParams.find((paramName) => !(templateParams[paramName] || '').trim())
    if (missingField) return `Ingresa ${WHATSAPP_TEMPLATE_PARAM_LABELS[missingField].toLowerCase()} para enviar la plantilla.`

    return ''
  }

  const buildPayload = () => ({
    to: to.trim(),
    template,
    params: requiredParams.reduce((acc, paramName) => {
      acc[paramName] = (templateParams[paramName] || '').trim()
      return acc
    }, {}),
  })

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (submitting) return

    const validationError = validateForm()
    if (validationError) {
      setFeedback({ type: 'error', text: validationError })
      return
    }

    setSubmitting(true)
    setFeedback(null)

    try {
      const response = await sendWhatsAppTemplate(buildPayload(), { useTestEndpoint: true })
      const provider = response?.provider || 'YCloud'
      const templateName = response?.templateName || response?.template || template
      const externalId = response?.externalId || response?.messageId || response?.id || 'N/A'
      setFeedback({
        type: 'success',
        text: `Plantilla enviada correctamente. Proveedor: ${provider}. Template: ${templateName}. ID: ${externalId}`,
      })
    } catch (error) {
      setFeedback({ type: 'error', text: error.message || 'No fue posible enviar la plantilla.' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box component="form" mt={2} onSubmit={handleSubmit}>
      <Typography variant="subtitle1" gutterBottom>
        Enviar plantilla de WhatsApp
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            required
            label="Número destino"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            disabled={submitting}
            helperText="Ej: +56912345678"
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth required variant="outlined" disabled={submitting}>
            <InputLabel id="whatsapp-template-label">Plantilla</InputLabel>
            <Select
              labelId="whatsapp-template-label"
              value={template}
              onChange={(event) => {
                setTemplate(event.target.value)
                setTemplateParams({})
                setFeedback(null)
              }}
              label="Plantilla">
              {Object.entries(WHATSAPP_TEMPLATES).map(([templateName, config]) => (
                <MenuItem value={templateName} key={templateName}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {requiredParams.map((paramName) => (
          <Grid item xs={12} md={paramName === 'itemsText' || paramName === 'surveyUrl' ? 12 : 6} key={paramName}>
            <TextField
              fullWidth
              required
              multiline={paramName === 'itemsText'}
              rows={paramName === 'itemsText' ? 3 : undefined}
              label={WHATSAPP_TEMPLATE_PARAM_LABELS[paramName]}
              value={templateParams[paramName] || ''}
              onChange={(event) => handleTemplateParamChange(paramName, event.target.value)}
              disabled={submitting}
              variant="outlined"
            />
          </Grid>
        ))}

        <Grid item xs={12}>
          <Paper variant="outlined" style={{ padding: 16, whiteSpace: 'pre-wrap' }}>
            <Typography variant="subtitle2" gutterBottom>
              Vista previa del mensaje
            </Typography>
            <Typography variant="body2">{previewText}</Typography>
            {templateConfig?.infoButtons?.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  Botones informativos configurados en YCloud:
                </Typography>
                <ul style={{ marginTop: 8 }}>
                  {templateConfig.infoButtons.map((button) => (
                    <li key={button}>
                      <Typography variant="body2">{button}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
            <Box mt={1}>
              <Typography variant="caption" color="textSecondary">
                Esta es una vista previa referencial. El mensaje final se envía mediante la plantilla aprobada en
                YCloud.
              </Typography>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Button type="submit" color="primary" variant="contained" disabled={submitting}>
            {submitting ? 'Enviando...' : 'Enviar plantilla'}
          </Button>
        </Grid>

        {feedback && (
          <Grid item xs={12}>
            <Alert severity={feedback.type}>{feedback.text}</Alert>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
